/**
 * Docs as typed blocks (slice 2g spec §3, §4): the kit's schemas, the writer
 * (`toMarkdown`), the reader (`parseBlocks`), the checker (`checkBlocks`) and
 * a doc's words (`plainText`). One module for the plugin's tool definitions,
 * the Worker, the SPA and the docs eval, so there's one kit.
 *
 * A doc's `body` stays markdown. Prose is itself; a data block is a fenced
 * code block whose info string is its type, holding the block without `type`
 * as JSON; a figure is `::figure{…}`; a callout, section, columns and column
 * are container directives (`:::section{label="…"}` … `:::`). The reader is a
 * line scanner, not remark-directive (which reads `7:30` in prose as a
 * directive), and it's total: whatever isn't a block is prose, and a block
 * that doesn't fit is "broken", read back as a `text` block of its source.
 *
 * It starts from the eval's variant (S-348, `test/eval/docs/variants/json/`, deleted once built in),
 * with §3.2's changes: optional `photo` and `url` on products, `url` on rows, `file:<uuid>`
 * ids, ceilings, `closing` only last, and `chart` stored with `kind`.
 */

// ── The schemas (§3.1) ──────────────────────────────────────────────────────

/** Markdown, at most 20,000 characters. */
const MD = { type: "string", maxLength: 20000 };
/** A line: no line break (normalised away), 1 to `max` characters. */
const LINE = (max) => ({ type: "string", minLength: 1, maxLength: max });
const FILE_ID = {
  type: "string",
  pattern: "^file:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$",
  description: "A file id you were given, never a URL.",
};
const URL_FIELD = { type: "string", format: "uri", maxLength: 2000 };
/** Where a thing in a doc came from: the page it was found on. */
const SOURCE_URL = { ...URL_FIELD, description: "The page you found it on." };
const PAIRS = {
  type: "array",
  minItems: 1,
  maxItems: 40,
  items: {
    type: "object",
    additionalProperties: false,
    required: ["label", "value"],
    properties: { label: LINE(80), value: LINE(300) },
  },
};
const block = (type, description, properties, required) => ({
  type: "object",
  additionalProperties: false,
  description,
  required: ["type", ...required],
  properties: { type: { const: type }, ...properties },
});

const TEXT = block(
  "text",
  "Prose in markdown: paragraphs, ## and ### headings, lists, links, GitHub tables. Never a # heading.",
  { markdown: MD },
  ["markdown"],
);
const CALLOUT = block(
  "callout",
  "A short aside the reader must not miss: a caveat (warning), a tip, or a note.",
  {
    tone: { enum: ["note", "tip", "warning"] },
    title: { ...LINE(60), description: "At most 5 words." },
    markdown: MD,
  },
  ["tone", "title", "markdown"],
);
const FIGURE = block("figure", "One image.", { src: FILE_ID, alt: LINE(200), caption: LINE(200) }, [
  "src",
  "alt",
]);
const FACTS = block(
  "facts",
  "2 to 6 headline figures shown big (a total, a count, a date).",
  {
    items: {
      type: "array",
      minItems: 2,
      maxItems: 6,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["label", "value"],
        properties: { label: LINE(60), value: LINE(80), note: LINE(120) },
      },
    },
  },
  ["items"],
);
const DETAILS = block(
  "details",
  "A label and value list: totals (Subtotal, Tax, Total), specs, a checklist against what was asked.",
  { items: PAIRS },
  ["items"],
);
const ROWS = block(
  "rows",
  "A list of things, each a row with a name, a line of detail and a value on the right (a price): options compared, a parts list.",
  {
    items: {
      type: "array",
      minItems: 1,
      maxItems: 100,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["name"],
        properties: {
          number: { type: "integer", minimum: 1, description: "For a ranked or numbered list." },
          thumbnail: {
            type: "object",
            additionalProperties: false,
            required: ["src", "alt"],
            properties: { src: FILE_ID, alt: LINE(200) },
          },
          name: LINE(120),
          detail: LINE(300),
          value: LINE(40),
          url: SOURCE_URL,
        },
      },
    },
  },
  ["items"],
);
const STEPS = block(
  "steps",
  "An ordered plan or itinerary.",
  {
    items: {
      type: "array",
      minItems: 1,
      maxItems: 50,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["title"],
        properties: { title: LINE(120), when: LINE(40), body: LINE(400) },
      },
    },
  },
  ["items"],
);
const MERCHANT = {
  type: "object",
  additionalProperties: false,
  required: ["name", "url"],
  properties: { name: LINE(80), url: SOURCE_URL },
};
const PRODUCT = block(
  "product",
  "One product as a card: its photo, price, a note, details, and where to buy it.",
  {
    number: { type: "integer", minimum: 1 },
    name: LINE(120),
    brand: LINE(80),
    price: { ...LINE(40), description: 'With its currency: "$489 CAD".' },
    note: LINE(600),
    photo: FILE_ID,
    details: PAIRS,
    merchant: MERCHANT,
  },
  ["name", "price", "note"],
);
const PRODUCTS = block(
  "products",
  "2 to 4 products compared side by side, with rows of the same attributes.",
  {
    items: {
      type: "array",
      minItems: 2,
      maxItems: 4,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["name", "price", "note"],
        properties: {
          name: LINE(80),
          brand: LINE(80),
          price: LINE(40),
          photo: FILE_ID,
          url: SOURCE_URL,
          note: LINE(200),
        },
      },
    },
    rows: {
      type: "array",
      minItems: 1,
      maxItems: 20,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["label", "values"],
        properties: {
          label: LINE(60),
          values: {
            type: "array",
            items: LINE(120),
            description: "One per item, in the items' order.",
          },
        },
      },
    },
  },
  ["items"],
);
const CHART = block(
  "chart",
  "Numbers easier to see than read.",
  {
    kind: { enum: ["bar", "line"] },
    title: LINE(80),
    unit: LINE(10),
    data: {
      type: "array",
      minItems: 1,
      maxItems: 24,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["label", "value"],
        properties: { label: LINE(60), value: { type: "number" } },
      },
    },
  },
  ["kind", "title", "unit", "data"],
);
const CLOSING = block(
  "closing",
  "The doc's last line: what was checked, when, and what wasn't done (\"Nothing was ordered.\").",
  { text: LINE(400) },
  ["text"],
);
const INNER = [TEXT, CALLOUT, FIGURE, FACTS, DETAILS, ROWS, STEPS, PRODUCT, PRODUCTS, CHART];
const COLUMNS = block(
  "columns",
  "2 or 3 short lists or paragraphs side by side (they stack on a phone).",
  {
    columns: {
      type: "array",
      minItems: 2,
      maxItems: 3,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["title", "blocks"],
        properties: {
          title: LINE(60),
          blocks: {
            type: "array",
            minItems: 1,
            maxItems: 20,
            items: { anyOf: [TEXT, CALLOUT, FIGURE] },
          },
        },
      },
    },
  },
  ["columns"],
);
const SECTION = block(
  "section",
  "A part of the doc under its own heading, holding its blocks.",
  {
    label: LINE(80),
    blocks: { type: "array", minItems: 1, maxItems: 100, items: { anyOf: [...INNER, COLUMNS] } },
  },
  ["label", "blocks"],
);

/** Every block's schema, by its type, in the order the reference lists them. */
export const BLOCK_SCHEMAS = Object.fromEntries(
  [SECTION, COLUMNS, ...INNER, CLOSING].map((s) => [s.properties.type.const, s]),
);
export const BLOCK_TYPES = Object.keys(BLOCK_SCHEMAS);

const TOP_MAX = 200;

/** `doc_create`'s and `doc_edit`'s `blocks`. */
export const BLOCKS_PARAM = {
  type: "array",
  minItems: 1,
  maxItems: TOP_MAX,
  description:
    "The doc's content, in order: prose in text blocks, sections for its parts, and Crew's blocks for anything richer. Crew draws them in its own style: no HTML or CSS.",
  items: { anyOf: [SECTION, COLUMNS, ...INNER, CLOSING] },
};

/**
 * A block the tools' schema has spelled out already: its type, and the rest
 * as `doc_create`'s blocks give it. Open, so its fields pass to the Worker,
 * which checks each block against its own schema (§6).
 */
const blockAsSaid = (types, description) => ({
  type: "object",
  additionalProperties: true,
  required: ["type"],
  description,
  properties: { type: { enum: types } },
});

const IN_SECTION_TYPES = [...INNER.map((s) => s.properties.type.const), "columns"];
const IN_COLUMN_TYPES = ["text", "callout", "figure"];

/** `s` with the schema at `path` (its keys from `s` down) replaced by `to`. */
function swapAt(s, [key, ...rest], to) {
  return { ...s, [key]: rest.length ? swapAt(s[key], rest, to) : to };
}

/**
 * `doc_create`'s `blocks` as the model is shown it: every block's schema
 * once, at the top. A section's and a column's blocks are the same ones,
 * so they're named by type, not said again: inlined, the kit came to
 * ~17,000 characters, most of a turn's tool definitions (S-429). Not a
 * `$ref`: OpenClaw inlines one again on the way to most providers, and
 * hands it raw to Codex and Claude Code. `BLOCKS_PARAM` stays the kit's,
 * for `checkBlocks`.
 */
export const BLOCKS_TOOL_PARAM = {
  ...BLOCKS_PARAM,
  items: {
    anyOf: [
      swapAt(
        SECTION,
        ["properties", "blocks", "items"],
        blockAsSaid(
          IN_SECTION_TYPES,
          "A block with its type's fields, as at the top: any but section and closing.",
        ),
      ),
      swapAt(
        COLUMNS,
        ["properties", "columns", "items", "properties", "blocks", "items"],
        blockAsSaid(IN_COLUMN_TYPES, "A text, callout or figure block, as at the top."),
      ),
      ...INNER,
      CLOSING,
    ],
  },
};

/**
 * `doc_edit`'s `append` and `blocks`: the same blocks as `doc_create`'s,
 * named by type rather than said a second and third time (S-429).
 */
export const blockList = (maxItems, description) => ({
  type: "array",
  minItems: 1,
  maxItems,
  description,
  items: blockAsSaid(
    BLOCK_TYPES,
    "A block with its type's fields, as doc_create's blocks take it.",
  ),
});

/**
 * The blocks' fields in a line each, written from their schemas, for the
 * tool descriptions (§3.3): Codex shows GPT a tool as a TypeScript
 * declaration, where the blocks union comes out as `unknown`, so the
 * description is the one place every harness shows the fields. `?` is
 * optional.
 */
function shape(s) {
  if (s.type === "array" && !s.anyOf) return `[${shape(s.items)}, …]`;
  if (s.type === "object") {
    const req = new Set(s.required ?? []);
    const fields = Object.entries(s.properties)
      .filter(([k]) => k !== "type")
      .map(([k, v]) => `${k}${req.has(k) ? "" : "?"}: ${shape(v)}`);
    return `{ ${fields.join(", ")} }`;
  }
  return leafShape(s);
}

/** A field that isn't a list or an object, as the reference names it. */
function leafShape(s) {
  if (s.const) return JSON.stringify(s.const);
  if (s.enum) return s.enum.join("|");
  if (s.anyOf) return "block";
  if (s.pattern === FILE_ID.pattern) return "file id";
  if (s.format === "uri") return "url";
  return s.type === "string" ? "text" : s.type;
}
export const BLOCKS_REF = Object.values(BLOCK_SCHEMAS)
  .map((b) => `${b.properties.type.const} ${shape(b)}`.replace(/ \{ /, " { type, "))
  .join("; ");

// ── Where a block may sit (§3.1) ────────────────────────────────────────────

const DATA_TYPES = new Set([
  "facts",
  "details",
  "rows",
  "steps",
  "product",
  "products",
  "chart",
  "closing",
]);
const IN_SECTION = new Set(IN_SECTION_TYPES);
const IN_COLUMN = new Set(IN_COLUMN_TYPES);

/** Why `type` can't sit `where` (`top`, `section` or `column`), or null. */
function misplaced(type, where, last) {
  if (where === "top") {
    return type === "closing" && !last ? "closing is only the last block" : null;
  }
  if (where === "section") {
    if (type === "section") return "a section can't hold a section";
    if (type === "closing") return "closing is only the last block of the doc, not in a section";
    return IN_SECTION.has(type) ? null : `a section can't hold a ${type}`;
  }
  return IN_COLUMN.has(type) ? null : `a column holds only text, callout or figure, not ${type}`;
}

/** What to do about a misplaced block, for the hint. */
function movedHint(type, where) {
  if (where === "section" && type === "section") return "Give it its own top-level section";
  if (where === "column") return `Give the ${type} block after the columns`;
  return null;
}

// ── Normalising (§4.3) ──────────────────────────────────────────────────────

const isObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const isLine = (s) => s.type === "string" && s.minLength === 1;

/**
 * `v` as its schema `s` wants it, so small slips cost no refusal: a line's
 * whitespace runs made one space and trimmed, a number given for a line made
 * its string, markdown trimmed, an optional field given as null left out,
 * and an object's keys in the schema's order. Anything else stays as it was,
 * for the checker to name.
 */
function normalise(v, s) {
  if (s.anyOf) {
    const own = isObject(v) ? BLOCK_SCHEMAS[v.type] : undefined;
    return own ? normalise(v, own) : v;
  }
  return (NORMALISE[s.type] ?? ((x) => x))(v, s);
}

function normaliseArray(v, s) {
  if (!Array.isArray(v)) return v;
  const items = v.map((x) => normalise(x, s.items));
  return s.items.anyOf ? mergeText(items) : items;
}

function normaliseString(v, s) {
  if (!isLine(s)) return typeof v === "string" ? v.trim() : v;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return typeof v === "string" ? v.replace(/\s+/g, " ").trim() : v;
}

const NORMALISE = {
  string: (v, s) => normaliseString(v, s),
  array: (v, s) => normaliseArray(v, s),
  object: (v, s) => (isObject(v) ? normaliseObject(v, s) : v),
};

/** Its fields in the schema's order, an optional null left out; fields it doesn't have stay, last, to be refused. */
function normaliseObject(v, s) {
  const out = {};
  const req = new Set(s.required ?? []);
  for (const [k, ks] of Object.entries(s.properties)) {
    const absent = !(k in v) || ((v[k] === null || v[k] === undefined) && !req.has(k));
    if (!absent) out[k] = normalise(v[k], ks);
  }
  for (const k of Object.keys(v)) if (!(k in s.properties)) out[k] = v[k];
  return out;
}

/**
 * A list of blocks as they'd read back once stored: no empty `text` block,
 * and a run of adjacent `text` blocks as the reader reads their prose,
 * joined by a blank line and split again only past 20,000 characters. So a
 * long doc read in pieces is given back as the same pieces.
 */
function mergeText(list) {
  const out = [];
  let run = [];
  const flush = () => {
    if (run.length === 1 && run[0].markdown) out.push(run[0]);
    if (run.length > 1) out.push(...proseBlocks(run.map((b) => b.markdown).join("\n\n")));
    run = [];
  };
  for (const b of list) {
    if (isPlainText(b)) {
      if (b.markdown) run.push(b);
    } else {
      flush();
      out.push(b);
    }
  }
  flush();
  return out;
}

const isPlainText = (b) =>
  isObject(b) && b.type === "text" && typeof b.markdown === "string" && Object.keys(b).length === 2;

// ── Checking (§6 steps 1–2) ─────────────────────────────────────────────────

const entries = (n) => (n === 1 ? "1 entry" : `${n} entries`);
const join = (p, k) => (p ? `${p}.${k}` : k);
const listed = (xs) =>
  xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} or ${xs.at(-1)}`;

/** What to use instead, when a list has the wrong number of entries. */
const COUNT_HINT = {
  "facts.items": "Use details for a single figure",
  "products.items": "Use product for a single one, and rows for more than 4",
  "columns.columns": "Use a section for one, and sections for more than 3",
};

/** Whether `s` is an absolute http(s) URL. */
function isHttpUrl(s) {
  if (!/^https?:\/\//i.test(s)) return false;
  try {
    const u = new URL(s);
    return (u.protocol === "https:" || u.protocol === "http:") && Boolean(u.host);
  } catch {
    return false;
  }
}

/**
 * The first way `v` doesn't fit `s`, as `{ why, more? }`, or null. `p` is its
 * path within the block (`items[0].name`); a block's `blocks` are checked by
 * `checkList`, which knows where each may sit. A small checker over what the
 * kit uses, not Ajv, so a hint is about this block only.
 */
function fit(v, s, p, type) {
  if (s.const !== undefined) return null;
  if (s.enum) return s.enum.includes(v) ? null : { why: `${p || "it"} is ${listed(s.enum)}` };
  return FITS[s.type]?.(v, s, p, type) ?? null;
}

const count = (n) => n.toLocaleString("en-US");

function fitString(v, s, p) {
  const name = p || "it";
  if (typeof v !== "string") {
    return { why: s.pattern ? `${name} is a file id (file:…)` : `${name} is text` };
  }
  if (s.minLength && v.length < s.minLength) return { why: `${name} is empty` };
  if (s.maxLength && v.length > s.maxLength) {
    return {
      why: `${name} is at most ${count(s.maxLength)} characters`,
      more: `it has ${count(v.length)}`,
    };
  }
  return fitFormat(v, s, name);
}

/** A file id's pattern and a URL's scheme. */
function fitFormat(v, s, name) {
  if (s.pattern && !new RegExp(s.pattern).test(v)) {
    return /^[a-z]+:\/\//i.test(v)
      ? { why: `${name} is a file id you were given (file:…), never a URL` }
      : { why: `${name} is a file id, file: and its uuid` };
  }
  if (s.format === "uri" && !isHttpUrl(v)) return { why: `${name} is an https: or http: URL` };
  return null;
}

function fitNumber(v, s, p) {
  const name = p || "it";
  if (typeof v !== "number" || !Number.isFinite(v)) return { why: `${name} is a number` };
  if (s.type === "integer" && !Number.isInteger(v)) return { why: `${name} is a whole number` };
  if (s.minimum !== undefined && v < s.minimum) return { why: `${name} is at least ${s.minimum}` };
  return null;
}

/** "2 to 6 entries", "at least 1 entry", "at most 20 entries". */
function rangeOf(lo, hi) {
  if (lo !== undefined && hi !== undefined) return `${lo} to ${hi} entries`;
  return lo !== undefined ? `at least ${entries(lo)}` : `at most ${entries(hi)}`;
}

function fitArray(v, s, p, type) {
  if (!Array.isArray(v)) return { why: `${p || "it"} is a list` };
  const counted = countProblem(v, s, p, type);
  if (counted || s.items.anyOf) return counted;
  for (let i = 0; i < v.length; i++) {
    const bad = fit(v[i], s.items, `${p}[${i}]`, type);
    if (bad) return bad;
  }
  return null;
}

/** A list with too few or too many entries, and what to use instead. */
function countProblem(v, s, p, type) {
  const { minItems: lo, maxItems: hi } = s;
  const fits = (lo === undefined || v.length >= lo) && (hi === undefined || v.length <= hi);
  if (fits) return null;
  const hint = v.length > 0 ? COUNT_HINT[`${type}.${p}`] : undefined;
  return {
    why: `${p || "it"} needs ${rangeOf(lo, hi)}`,
    more: `it has ${v.length}${hint ? `. ${hint}` : ""}`,
  };
}

function fitObject(v, s, p, type) {
  const name = p || "it";
  if (!isObject(v)) return { why: `${name} is an object` };
  const missing = (s.required ?? []).find((k) => !(k in v));
  if (missing) return { why: `${name} has no ${missing}` };
  const extra = Object.keys(v).find((k) => !(k in s.properties));
  if (extra) {
    const fields = Object.keys(s.properties).filter((k) => k !== "type");
    return { why: `${join(p, extra)} isn't one of its fields`, more: `they're ${listed(fields)}` };
  }
  for (const [k, ks] of Object.entries(s.properties)) {
    const bad = k in v ? fit(v[k], ks, join(p, k), type) : null;
    if (bad) return bad;
  }
  return null;
}

const FITS = {
  string: fitString,
  number: fitNumber,
  integer: fitNumber,
  array: fitArray,
  object: fitObject,
};

/** A refusal's parts: where, the block's type, why, and the whole hint. */
function problem(at, type, why, more) {
  const head = type ? `${at} (${type})` : at;
  // A sentence of its own ("Give it …") after a full stop; a detail ("it has 1") after a semicolon.
  const sep = more && /^[A-Z]/.test(more) ? ". " : "; ";
  return { at, type, why, hint: `${head}: ${why}${more ? `${sep}${more}` : ""}.` };
}

/** What a block's own fields (not its children) fail, as `{ why, more? }`, or null. */
function ownFit(b) {
  const bad = fit(b, BLOCK_SCHEMAS[b.type], "", b.type);
  if (bad || b.type !== "products" || !Array.isArray(b.rows)) return bad;
  const n = b.items.length;
  const off = b.rows.find((r) => r.values.length !== n);
  return off
    ? { why: `each row needs one value per item (${n}); "${off.label}" has ${off.values.length}` }
    : null;
}

/**
 * The first problem in `list`, blocks that sit `where`, at path `at`: an
 * unknown type, a block where it can't sit, its fields, its children, a
 * `text` block holding what reads as a block, and a top-level block's stored
 * form past 60,000 characters. Null when it all fits. `kept` is what the doc
 * holds already (`storedOf`), or null for a new one.
 */
function checkList(list, where, at, kept) {
  for (let i = 0; i < list.length; i++) {
    const found = checkItem(list[i], where, `${at}[${i}]`, i === list.length - 1, kept);
    if (found) return found;
  }
  return null;
}

function checkItem(b, where, path, last, kept) {
  if (!isObject(b) || !BLOCK_SCHEMAS[b.type]) {
    return problem(path, null, `a block's type is one of ${BLOCK_TYPES.join(", ")}`);
  }
  const away = misplaced(b.type, where, last);
  if (away) return problem(path, b.type, away, movedHint(b.type, where));
  // A text the doc holds already isn't refused for its length: a person's long line stays saveable.
  const bad = ownFit(keptText(b, kept) ? { ...b, markdown: "" } : b);
  if (bad) return problem(path, b.type, bad.why, bad.more);
  return (
    childProblem(b, path, kept) ??
    heldProblem(b, path, kept) ??
    (where === "top" ? sizeProblem(b, path) : null)
  );
}

/** A `text` block past 20,000 characters whose markdown is in the doc already, as it was read. */
const keptText = (b, kept) =>
  Boolean(kept) &&
  b.type === "text" &&
  typeof b.markdown === "string" &&
  b.markdown.length > TEXT_MAX &&
  kept.body.includes(b.markdown);

/** The first problem among a section's or columns' blocks. */
function childProblem(b, path, kept) {
  if (b.type === "section") return checkList(b.blocks, "section", `${path}.blocks`, kept);
  if (b.type !== "columns") return null;
  for (let j = 0; j < b.columns.length; j++) {
    const inner = checkList(b.columns[j].blocks, "column", `${path}.columns[${j}].blocks`, kept);
    if (inner) return inner;
  }
  return null;
}

/** A `text` block whose markdown reads as a block once stored, other than a broken one the doc holds. */
function heldProblem(b, path, kept) {
  const held = b.type === "text" ? heldBlock(b.markdown, kept) : null;
  return held
    ? problem(
        path,
        "text",
        `its markdown holds a ${held} block`,
        `Give it as its own ${held} block, after this text`,
      )
    : null;
}

function sizeProblem(b, path) {
  const size = write(b).text.length;
  return size > BLOCK_MAX
    ? problem(
        path,
        b.type,
        `its stored form is ${count(size)} characters, past ${count(BLOCK_MAX)}`,
        "Split it into sections",
      )
    : null;
}

/**
 * The type of the first block `markdown` reads as, stored alone, or null for
 * plain prose. A broken block whose source the doc holds already (`kept`) is
 * prose here: given back as it was read, it stays as it was (§4.4, §6).
 */
function heldBlock(markdown, kept) {
  const read = parseBlocks(markdown);
  if (
    read.length === 1 &&
    read[0].type === "text" &&
    !read[0].broken &&
    read[0].markdown === markdown
  ) {
    return null;
  }
  const odd = read.find((b) => b.type !== "text" || (b.broken && !kept?.broken.has(b.markdown)));
  if (odd) return odd.broken?.type ?? odd.type;
  return kept ? null : "text";
}

/** One top-level block's stored form, at most (§5.6). */
const BLOCK_MAX = 60000;

/**
 * An agent's `blocks`, normalised, and the first problem in them or null
 * (§6 steps 1–2): each block checked against its schema by its type, where
 * it sits, `closing` last, `products` rows one value per item, a `url`'s
 * scheme, a `text` block that holds a block, and the size of a top-level
 * block. File ids, the doc's size and the round trip are the save's.
 *
 * `opts.kept` is the doc as it is (`storedOf(body)`), for a write to an
 * existing doc: a text it holds already isn't refused for its length, nor a
 * broken block it holds for being one (§6, "Blocks already in the doc").
 * `opts.root` names the list in a hint: `blocks` (the default) or `append`.
 */
export function checkBlocks(input, opts = {}) {
  const root = opts.root ?? "blocks";
  if (!Array.isArray(input)) {
    return { blocks: null, problem: problem(root, null, `${root} is a list of blocks`) };
  }
  const blocks = normalise(input, BLOCKS_PARAM);
  if (blocks.length < 1 || blocks.length > TOP_MAX) {
    return {
      blocks,
      problem: problem(
        root,
        null,
        `${root} needs 1 to ${TOP_MAX} blocks`,
        `it has ${blocks.length}`,
      ),
    };
  }
  return { blocks, problem: checkList(blocks, "top", root, opts.kept ?? null) };
}

/**
 * What a doc holds already, for `checkBlocks`' `kept`: its body, and the
 * source of each broken block in it.
 */
export function storedOf(body) {
  const text = String(body ?? "");
  const broken = new Set();
  eachBlock(parseBlocks(text), (b) => {
    if (b.broken) broken.add(b.markdown);
  });
  return { body: text, broken };
}

/** `fn` on every block in `list`, nested ones too, in reading order, with its path from `at`. */
function eachBlock(list, fn, at = "blocks") {
  list.forEach((b, i) => {
    const path = `${at}[${i}]`;
    fn(b, path);
    if (b.type === "section") eachBlock(b.blocks, fn, `${path}.blocks`);
    if (b.type === "columns") {
      b.columns.forEach((c, j) => eachBlock(c.blocks, fn, `${path}.columns[${j}].blocks`));
    }
  });
}

/**
 * Whether normalised `blocks` read back from `body`, what they were written
 * as, exactly (§4.5): null, or a problem naming the first block that doesn't.
 * A broken block given back as text reads back broken, and counts as its text.
 */
export function roundTripProblem(blocks, body) {
  const back = normalise(readBlocks(body), BLOCKS_PARAM);
  const i = blocks.findIndex((b, k) => JSON.stringify(b) !== JSON.stringify(back[k]));
  if (i < 0 && back.length === blocks.length) return null;
  const at = i < 0 ? blocks.length : i;
  const type = blocks[at]?.type ?? null;
  return problem(
    `blocks[${at}]`,
    type,
    "it doesn't read back as written",
    type === "text"
      ? "Give any block in its markdown as a block of its own"
      : "Give it again as its schema says",
  );
}

/** The file ids in `blocks` (§7), each with its block's path and type, in reading order. */
export function fileRefs(blocks, root = "blocks") {
  const out = [];
  eachBlock(
    blocks,
    (b, at) => {
      for (const id of filesOf(b)) out.push({ id, at, type: b.type });
    },
    root,
  );
  return out;
}

/** A block's own file ids: a figure's, rows' thumbnails, a product's photo, products' photos. */
function filesOf(b) {
  const ids = {
    figure: () => [b.src],
    rows: () => (b.items ?? []).map((x) => x?.thumbnail?.src),
    product: () => [b.photo],
    products: () => (b.items ?? []).map((x) => x?.photo),
  }[b.type]?.();
  return (ids ?? []).filter((id) => typeof id === "string");
}

/**
 * Blocks by type, nested ones counted, for a step on Activity (§5.7):
 * "12 blocks: 3 section, 4 rows, 1 details". Never the blocks themselves.
 */
export function blockTally(list) {
  if (!Array.isArray(list)) return null;
  const by = new Map();
  tally(list, by);
  const n = [...by.values()].reduce((a, k) => a + k, 0);
  const kinds = [...by].map(([type, k]) => `${k} ${type}`).join(", ");
  return `${n} ${n === 1 ? "block" : "blocks"}${kinds ? `: ${kinds}` : ""}`;
}

/** Counts `xs`' blocks into `by`, by type, nested ones too; anything else is "unknown". */
function tally(xs, by) {
  for (const b of xs) {
    const type = isObject(b) && typeof b.type === "string" ? b.type.slice(0, 20) : "unknown";
    by.set(type, (by.get(type) ?? 0) + 1);
    for (const inner of innerLists(b)) tally(inner, by);
  }
}

/** A block's lists of blocks, as given: a section's, each column's. */
function innerLists(b) {
  if (b?.type === "section") return Array.isArray(b.blocks) ? [b.blocks] : [];
  if (b?.type !== "columns" || !Array.isArray(b.columns)) return [];
  return b.columns.map((c) => c?.blocks).filter(Array.isArray);
}

// ── Writing (§4.1) ──────────────────────────────────────────────────────────

const attr = (v) => String(v).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
const unattr = (v) => v.replace(/&quot;/g, '"').replace(/&amp;/g, "&");
const attrs = (b, keys) =>
  keys
    .filter((k) => b[k] !== undefined)
    .map((k) => `${k}="${attr(b[k])}"`)
    .join(" ");

/** The longest run of colons that opens a line of `md`: a container around it needs more. */
function colonRun(md) {
  let n = 0;
  for (const m of md.matchAll(/^(:{3,})/gm)) n = Math.max(n, m[1].length);
  return n;
}

/** A data block's fence: 3 backticks, or one more than the longest run in its JSON. */
function dataFence(type, data) {
  const json = JSON.stringify(data, null, 2);
  const longest = Math.max(0, ...[...json.matchAll(/`+/g)].map((m) => m[0].length));
  const ticks = "`".repeat(Math.max(3, longest + 1));
  return `${ticks}${type}\n${json}\n${ticks}`;
}

/**
 * A container: its fence is 3 colons, or one more than the longest fence
 * among its children (a text child's longest colon line counts too).
 */
function container(head, children) {
  const inner = Math.max(0, ...children.map((c) => c.fence));
  const len = Math.max(3, inner ? inner + 1 : 0);
  const f = ":".repeat(len);
  const body = children.map((c) => c.text).join("\n\n");
  return { text: `${f}${head}\n${body}\n${f}`, fence: len };
}

/** One normalised block as stored, and the fence a container around it must pass. */
function write(b) {
  switch (b.type) {
    case "text":
      return { text: b.markdown, fence: colonRun(b.markdown) };
    case "callout":
      return container(`callout{${attrs(b, ["tone", "title"])}}`, [
        { text: b.markdown, fence: colonRun(b.markdown) },
      ]);
    case "figure":
      return { text: `::figure{${attrs(b, ["src", "alt", "caption"])}}`, fence: 0 };
    case "section":
      return container(`section{${attrs(b, ["label"])}}`, b.blocks.map(write));
    case "columns":
      return container(
        "columns",
        b.columns.map((c) => container(`column{${attrs(c, ["title"])}}`, c.blocks.map(write))),
      );
    default: {
      const { type, ...data } = b;
      return { text: dataFence(type, data), fence: 0 };
    }
  }
}

/**
 * `blocks` as a doc's stored body (§4.1): normalised, each block in its
 * syntax, one blank line between them, no trailing newline. The blocks are
 * checked first (`checkBlocks`); a block of no known type throws.
 */
export function toMarkdown(blocks) {
  const list = normalise(blocks, BLOCKS_PARAM);
  return list
    .map((b) => {
      if (!isObject(b) || !BLOCK_SCHEMAS[b.type]) {
        throw new Error(`a block's type is one of ${BLOCK_TYPES.join(", ")}`);
      }
      return write(b).text;
    })
    .join("\n\n");
}

// ── Reading (§4.2) ──────────────────────────────────────────────────────────

/** A prose run past this is split into text blocks that each fit the kit. */
const TEXT_MAX = 20000;

const CODE_OPEN = /^ {0,3}(`{3,}|~{3,})(.*)$/;
const DATA_OPEN = /^(`{3,})([a-z]+)$/;
const CONTAINER_OPEN = /^(:{3,})(callout|section|columns|column)(?:\{(.*)\})?$/;
const FIGURE_LINE = /^::figure\{(.*)\}$/;

/** A line without its carriage return: a CRLF body reads as an LF one. */
const bare = (l) => (l.endsWith("\r") ? l.slice(0, -1) : l);

/**
 * Where the code fence opened at `i` closes, before `to`: the next line of
 * its marker, as long or longer, and nothing else; -1 for none. A backtick
 * fence's info string has no backtick, or it isn't a fence.
 */
function codeEnd(lines, i, to) {
  const m = CODE_OPEN.exec(bare(lines[i]));
  if (!m || (m[1][0] === "`" && m[2].includes("`"))) return -2;
  const closer = new RegExp(`^ {0,3}${m[1][0] === "`" ? "`" : "~"}{${m[1].length},}[ \\t]*$`);
  for (let j = i + 1; j < to; j++) if (closer.test(bare(lines[j]))) return j;
  return -1;
}

/**
 * The line closing the container opened at `i` with `colons`: the next line
 * of exactly those colons, past any code fence that closes; -1 for none.
 */
function containerEnd(lines, i, to, colons) {
  for (let j = i + 1; j < to; j++) {
    const l = bare(lines[j]);
    if (l === colons) return j;
    const end = codeEnd(lines, j, to);
    if (end >= 0) j = end;
  }
  return -1;
}

/** `name="value"` pairs, one space apart, as an object; null when they don't read. */
function readAttrs(s) {
  if (s === undefined || s === "") return {};
  const out = {};
  const re = /([a-z]+)="([^"]*)"( |$)/gy;
  let at = 0;
  for (let m = re.exec(s); m; m = re.exec(s)) {
    out[m[1]] = unattr(m[2]);
    at = re.lastIndex;
    if (m[3] === "") break;
  }
  return at === s.length ? out : null;
}

/**
 * Prose as text blocks: trimmed, an empty one dropped, and a run past 20,000
 * characters split at blank lines outside code fences, else at line breaks,
 * so each fits a `text` block (only a single line past 20,000 stays whole).
 */
function proseBlocks(text) {
  const t = text.trim();
  if (!t) return [];
  if (t.length <= TEXT_MAX) return [{ type: "text", markdown: t }];
  // A paragraph past 20,000 goes line by line; pieces keep what joined them.
  const pieces = paragraphs(t).flatMap((p) =>
    p.length <= TEXT_MAX
      ? [{ text: p, sep: "\n\n" }]
      : p.split("\n").map((line, k) => ({ text: line, sep: k ? "\n" : "\n\n" })),
  );
  return pack(pieces)
    .map((m) => m.trim())
    .filter(Boolean)
    .map((markdown) => ({ type: "text", markdown }));
}

/** The code fence open after `line`: `fence` still, closed, or one it opens. */
function fenceAfter(fence, line) {
  const m = CODE_OPEN.exec(bare(line));
  if (!fence) return m ? m[1] : null;
  const closes = m && m[1][0] === fence[0] && m[1].length >= fence.length && !m[2].trim();
  return closes ? null : fence;
}

/** Prose's paragraphs: split at blank lines outside code fences. */
function paragraphs(t) {
  const paras = [];
  let cur = [];
  let fence = null;
  for (const line of t.split("\n")) {
    fence = fenceAfter(fence, line);
    const blank = line.trim() === "";
    if (!fence && blank && cur.length) {
      paras.push(cur.join("\n"));
      cur = [];
    } else if (!blank || cur.length) cur.push(line);
  }
  if (cur.length) paras.push(cur.join("\n"));
  return paras;
}

/** Pieces joined into chunks of at most 20,000 characters, greedily, in order. */
function pack(pieces) {
  const out = [];
  let chunk = "";
  for (const { text, sep } of pieces) {
    if (chunk && chunk.length + sep.length + text.length > TEXT_MAX) {
      out.push(chunk);
      chunk = text;
    } else chunk = chunk ? chunk + sep + text : text;
  }
  if (chunk) out.push(chunk);
  return out;
}

/** A broken block: its source as a `text` block, with what it was meant to be and why not. */
const brokenBlock = (source, type, why, line) => ({
  type: "text",
  markdown: source,
  broken: { type, why, line },
});

/**
 * Lines `[from, to)` as blocks that sit `where` (`top`, `section` or
 * `column`); a broken block's line is its index in `lines`, plus one.
 */
function readRange(lines, from, to, where) {
  const out = [];
  let prose = [];
  for (let i = from; i < to;) {
    const step = readAt(lines, i, to);
    if (step.block) {
      out.push(...proseBlocks(prose.join("\n")), step.block);
      prose = [];
    } else prose.push(...lines.slice(i, step.next));
    i = step.next;
  }
  out.push(...proseBlocks(prose.join("\n")));
  return placed(out, where);
}

/**
 * What starts at line `i`: a block and the line after it, or prose up to
 * `next` (an ordinary code block whole, or, from any opener with no closer,
 * the rest of the range).
 */
function readAt(lines, i, to) {
  const l = bare(lines[i]);
  const data = DATA_OPEN.exec(l);
  if (data && DATA_TYPES.has(data[2])) {
    const end = lines.slice(i + 1, to).findIndex((x) => bare(x) === data[1]);
    if (end < 0) return { next: to };
    const j = i + 1 + end;
    const source = lines.slice(i, j + 1).join("\n");
    return {
      block: dataBlock(data[2], source, lines.slice(i + 1, j).join("\n"), i + 1),
      next: j + 1,
    };
  }
  const open = CONTAINER_OPEN.exec(l);
  if (open) {
    const j = containerEnd(lines, i, to, open[1]);
    return j < 0
      ? { next: to }
      : { block: containerBlock(lines, i, j, open[2], open[3]), next: j + 1 };
  }
  const fig = FIGURE_LINE.exec(l);
  if (fig) return { block: figureBlock(lines[i], fig[1], i + 1), next: i + 1 };
  // An ordinary code block is prose to its closer; one with none is code to the end, as markdown reads it.
  const end = codeEnd(lines, i, to);
  return { next: end >= 0 ? end + 1 : end === -1 ? to : i + 1 };
}

/** Blocks that can't sit `where` made broken, in place. */
function placed(list, where) {
  return list.map((b, i) => {
    if (b.type === "text") return b;
    const away = misplaced(b.type, where, i === list.length - 1);
    return away ? brokenBlock(b.source, b.type, away, b.line) : b;
  });
}

/** A block read from its source, with where it came from (taken off before it's given out). */
const read = (b, source, line) =>
  Object.defineProperties(b, {
    source: { value: source, enumerable: false },
    line: { value: line, enumerable: false },
  });

function dataBlock(type, source, json, line) {
  let data;
  try {
    data = JSON.parse(json);
  } catch (e) {
    const why = String(e?.message ?? e).split("\n")[0];
    return brokenBlock(source, type, `its JSON doesn't parse (${why})`, line);
  }
  if (!isObject(data)) return brokenBlock(source, type, "its JSON isn't an object", line);
  if ("type" in data)
    return brokenBlock(source, type, "its JSON has a type; the fence names it", line);
  const b = normalise({ type, ...data }, BLOCK_SCHEMAS[type]);
  const bad = ownFit(b);
  return bad ? brokenBlock(source, type, bad.why, line) : read(b, source, line);
}

function figureBlock(source, inner, line) {
  const a = readAttrs(inner);
  if (!a) return brokenBlock(source, "figure", "its attributes don't read", line);
  const b = normalise({ type: "figure", ...a }, FIGURE);
  const bad = ownFit(b);
  return bad ? brokenBlock(source, "figure", bad.why, line) : read(b, source, line);
}

/** The container from line `i` to its closer `j`. */
function containerBlock(lines, i, j, name, rawAttrs) {
  const at = { lines, i, j, source: lines.slice(i, j + 1).join("\n") };
  const a = readAttrs(rawAttrs);
  if (!a) return brokenAt(at, name, "its attributes don't read");
  if (name === "callout") {
    const markdown = lines.slice(i + 1, j).join("\n");
    return checkedAt(at, normalise({ type: "callout", ...a, markdown }, CALLOUT));
  }
  if (name === "section") {
    const b = normalise({ type: "section", ...a }, SECTION);
    b.blocks = readRange(lines, i + 1, j, "section");
    return checkedAt(at, b);
  }
  // Only `columns` reads its columns; one anywhere else is out of place.
  if (name === "column") return brokenAt(at, name, "a column goes only in a columns block");
  if (Object.keys(a).length) return brokenAt(at, name, "columns has no attributes");
  return columnsBlock(at);
}

const brokenAt = (at, type, why) => brokenBlock(at.source, type, why, at.i + 1);

/** `b` read from `at`, or broken by what its own fields fail. */
function checkedAt(at, b) {
  const bad = ownFit(b);
  return bad ? brokenAt(at, b.type, bad.why) : read(b, at.source, at.i + 1);
}

/** A columns block: only columns inside it, blank lines between. */
function columnsBlock(at) {
  const { lines, i, j } = at;
  const columns = [];
  for (let k = i + 1; k < j; k++) {
    if (bare(lines[k]).trim() === "") continue;
    const open = CONTAINER_OPEN.exec(bare(lines[k]));
    const end = open?.[2] === "column" ? containerEnd(lines, k, j, open[1]) : -1;
    if (end < 0) return brokenAt(at, "columns", "a columns block holds only columns");
    const ca = readAttrs(open[3]);
    if (!ca) return brokenAt(at, "columns", `columns[${columns.length}]'s attributes don't read`);
    const col = normalise({ ...ca }, COLUMNS.properties.columns.items);
    col.blocks = readRange(lines, k + 1, end, "column");
    columns.push(col);
    k = end;
  }
  return checkedAt(at, { type: "columns", columns });
}

/**
 * A doc's body as blocks (§4.2): prose as `text` blocks, each block in its
 * syntax as itself, and a broken one as a `text` block of its source with
 * `broken: { type, why, line }` (the line its source starts on). Never
 * throws. A top-level section or columns whose stored form passes 60,000
 * characters reads as its source, as long prose does, so no block passes a
 * page of `doc_read`.
 */
export function parseBlocks(body) {
  const lines = String(body ?? "").split("\n");
  const top = readRange(lines, 0, lines.length, "top");
  return top.flatMap((b) =>
    (b.type === "section" || b.type === "columns") && b.source.length > BLOCK_MAX
      ? proseBlocks(b.source)
      : [b],
  );
}

/** Every broken block in `blocks`, nested ones too, in reading order. */
export function brokenBlocks(blocks) {
  const out = [];
  eachBlock(blocks, (b) => {
    if (b.broken) out.push(b.broken);
  });
  return out;
}

/**
 * Broken blocks in `body` that `kept` (`storedOf`, the doc as it was) doesn't
 * hold, each with its source, in reading order: what a body write would add (§6 step 6).
 */
export function newBroken(body, kept) {
  const out = [];
  eachBlock(parseBlocks(body), (b) => {
    if (b.broken && !kept?.broken.has(b.markdown)) out.push({ ...b.broken, source: b.markdown });
  });
  return out;
}

/**
 * A doc's body as the agent reads it (§5.2): `parseBlocks`, with a broken
 * block a plain `text` block of its source (no `broken`), as plain data.
 */
export function readBlocks(body) {
  return JSON.parse(JSON.stringify(parseBlocks(body), (k, v) => (k === "broken" ? undefined : v)));
}

// ── Plain text (§4.6) ───────────────────────────────────────────────────────

/** One line of markdown as the words it shows. */
function plainLine(line) {
  const t = line.trim();
  if (/^([-*_=]\s*){3,}$/.test(t) || /^\|?[\s:|-]+\|?$/.test(t)) return "";
  return t
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^(#{1,6}\s+|>\s*|[-*+]\s+(\[[ xX]\]\s+)?|\d+[.)]\s+)+/, "")
    .replace(/\*\*|__|~~|`/g, "")
    .replace(/(^|[^\w\\])[*_](?=\S)|(?<=\S)[*_](?=[^\w]|$)/g, "$1")
    .replace(/\\([\\`*_{}[\]()#+\-.!>~|])/g, "$1")
    .replace(/\s*\|\s*/g, " · ")
    .replace(/^· | ·$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Markdown's lines as words: code fences' markers go, their lines stay. */
function markdownWords(md) {
  return md
    .split("\n")
    .filter((l) => !CODE_OPEN.test(bare(l)))
    .map(plainLine)
    .filter(Boolean);
}

/** A block's words, in reading order: its line fields joined by " · ", one line a block. */
export function blockWords(b) {
  const j = (xs) => xs.filter((x) => typeof x === "string" && x).join(" · ");
  const pairs = (items) => (items ?? []).flatMap((x) => [x.label, x.value]);
  switch (b.type) {
    case "text":
      return b.broken ? [] : markdownWords(b.markdown);
    case "callout":
      return [j([b.title, ...markdownWords(b.markdown)])];
    case "figure":
      return [b.alt];
    case "section":
      return [b.label, ...b.blocks.flatMap(blockWords)];
    case "columns":
      return b.columns.flatMap((c) => [c.title, ...c.blocks.flatMap(blockWords)]);
    case "facts":
      return [j(b.items.flatMap((x) => [x.label, x.value, x.note]))];
    case "details":
      return [j(pairs(b.items))];
    case "rows":
      return [j(b.items.flatMap((x) => [x.name, x.detail, x.value]))];
    case "steps":
      return [j(b.items.flatMap((x) => [x.when, x.title, x.body]))];
    case "product":
      return [j([b.name, b.brand, b.price, b.note, ...pairs(b.details), b.merchant?.name])];
    case "products":
      return [
        j([
          ...b.items.flatMap((x) => [x.name, x.brand, x.price, x.note]),
          ...(b.rows ?? []).flatMap((r) => [r.label, ...r.values]),
        ]),
      ];
    case "chart":
      return [j([b.title, ...b.data.map((d) => d.label)])];
    case "closing":
      return [b.text];
    default:
      return [];
  }
}

/**
 * A doc as words (§4.6), for cards, snippets and a link's preview: text as
 * its words, each other block's line fields, a line each; never JSON, and a
 * broken block left out. With `title`, a first heading that is the title
 * goes (the card shows the title already).
 */
export function plainText(body, opts = {}) {
  const blocks = parseBlocks(body);
  const first = blocks[0];
  const name = opts.title?.trim().toLowerCase();
  if (name && first?.type === "text" && !first.broken) {
    const [head, ...rest] = first.markdown.split("\n");
    if (/^#{1,6}\s/.test(head.trim()) && plainLine(head).toLowerCase() === name) {
      blocks[0] = { type: "text", markdown: rest.join("\n") };
    }
  }
  return blocks
    .flatMap(blockWords)
    .filter((l) => l && l.trim())
    .join("\n");
}
